/**
 * Parse and serialize Awards section content for structured card editing (Kickresume-style).
 */

export interface AwardEntry {
  id: string;
  title?: string;
  issuer?: string;
  url?: string;
  date?: string;
  description?: string;
}

const AWARDS_JSON_PREFIX = '___AWARDS_JSON___';

function generateId(): string {
  return `award-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;
}

export function parseAwardsContent(content: string): AwardEntry[] {
  if (!content || !content.trim()) return [{ id: generateId() }];
  const trimmed = content.trim();
  if (trimmed.startsWith(AWARDS_JSON_PREFIX)) {
    try {
      const json = trimmed.slice(AWARDS_JSON_PREFIX.length).trim();
      const arr = JSON.parse(json) as unknown;
      if (Array.isArray(arr) && arr.length > 0) {
        return arr.map((item: any) => ({
          id: typeof item.id === 'string' ? item.id : generateId(),
          title: typeof item.title === 'string' ? item.title : '',
          issuer: typeof item.issuer === 'string' ? item.issuer : '',
          url: typeof item.url === 'string' ? item.url : '',
          date: typeof item.date === 'string' ? item.date : '',
          description: typeof item.description === 'string' ? item.description : '',
        }));
      }
    } catch {
      // fall through
    }
  }
  const lines = content.split(/\n/).map((l) => l.trim()).filter(Boolean);
  return [{ id: generateId(), title: lines[0] || '', description: lines.slice(1).join('\n') || '' }];
}

export function serializeAwardsContent(entries: AwardEntry[]): string {
  const list = entries.map((e) => ({
    id: e.id,
    title: e.title ?? '',
    issuer: e.issuer ?? '',
    url: e.url ?? '',
    date: e.date ?? '',
    description: e.description ?? '',
  }));
  return AWARDS_JSON_PREFIX + JSON.stringify(list);
}

export function isAwardsJsonContent(content: string): boolean {
  return typeof content === 'string' && content.trim().startsWith(AWARDS_JSON_PREFIX);
}

export function awardsEntriesToDisplayText(entries: AwardEntry[]): string {
  return entries
    .map((e) => {
      const parts: string[] = [];
      if (e.title) parts.push(e.title);
      if (e.issuer) parts.push(e.issuer);
      if (e.date) parts.push(e.date);
      const header = parts.join(' · ');
      const desc = (e.description || '').trim();
      return desc ? `${header}\n${desc}` : header;
    })
    .join('\n\n');
}

export function getAwardsDisplayContent(content: string): string {
  if (isAwardsJsonContent(content)) {
    return awardsEntriesToDisplayText(parseAwardsContent(content));
  }
  return content;
}
